/**
 * VerpflegungAdmin — Admin-Bereich fuer Mitarbeiterverpflegung
 * - Beleg-Upload (Kassenbons mit Drag & Drop)
 * - Monats-Auswertung (Sachbezugswerte pro Mitarbeiter)
 * - Export fuer Steuerberater (ZIP-Download)
 */

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBelege, useMonatsAuswertung, uploadBelegDatei } from '@/hooks/useVerpflegung';
import { localIsoDate } from '@/lib/date-utils';
import { SACHBEZUGSWERT_PRO_TAG } from '@/lib/database.types';
import type { VerpflegungFirma } from '@/lib/database.types';
import {
  Upload, FileText, Trash2, Download, Calendar, Building2,
  Loader2, Receipt, BarChart3, Image,
} from 'lucide-react';
import { toast } from 'sonner';

const KOOP_RED = '#C41E3A';

// ─── BELEG UPLOAD TAB ────────────────────────────────────────────────────────

function BelegUploadTab({ firma }: { firma: VerpflegungFirma }) {
  const { profile } = useAuth();
  const { belege, loading, erstelleBeleg, loescheBeleg } = useBelege(firma);
  const [datum, setDatum] = useState(localIsoDate(new Date()));
  const [betragMahlzeiten, setBetragMahlzeiten] = useState('');
  const [betragAufmerksamkeiten, setBetragAufmerksamkeiten] = useState('');
  const [notizen, setNotizen] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [belegUrl, setBelegUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Nur Bilder oder PDFs erlaubt');
      return;
    }
    setUploadingFile(true);
    const url = await uploadBelegDatei(file);
    setUploadingFile(false);
    if (url) {
      setBelegUrl(url);
      toast.success('Beleg hochgeladen');
    } else {
      toast.error('Upload fehlgeschlagen');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleSpeichern = async () => {
    if (!datum) {
      toast.error('Bitte Datum angeben');
      return;
    }
    setSaving(true);
    const ok = await erstelleBeleg({
      datum,
      firma,
      betrag_mahlzeiten: parseFloat(betragMahlzeiten) || 0,
      betrag_aufmerksamkeiten: parseFloat(betragAufmerksamkeiten) || 0,
      beleg_url: belegUrl || undefined,
      notizen: notizen || undefined,
      hochgeladen_von: profile?.id,
    });
    setSaving(false);
    if (ok) {
      toast.success('Beleg gespeichert');
      setBetragMahlzeiten('');
      setBetragAufmerksamkeiten('');
      setNotizen('');
      setBelegUrl(null);
    } else {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload-Formular */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Receipt className="w-4 h-4" style={{ color: KOOP_RED }} />
          Neuen Kassenbon erfassen
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-medium text-gray-500 block mb-1">Datum</label>
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 block mb-1">Betrag Mahlzeiten (EUR)</label>
            <input type="number" step="0.01" placeholder="0.00" value={betragMahlzeiten}
              onChange={e => setBetragMahlzeiten(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 block mb-1">Betrag Aufmerksamkeiten (EUR)</label>
            <input type="number" step="0.01" placeholder="0.00" value={betragAufmerksamkeiten}
              onChange={e => setBetragAufmerksamkeiten(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 block mb-1">Notizen</label>
            <input type="text" placeholder="Optional" value={notizen}
              onChange={e => setNotizen(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
          </div>
        </div>

        {/* Drag & Drop Beleg-Upload */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`mt-3 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[#C41E3A] bg-red-50/30' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {uploadingFile ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
          ) : belegUrl ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600">
              <Image className="w-4 h-4" />
              <span className="text-xs font-medium">Beleg hochgeladen</span>
            </div>
          ) : (
            <>
              <Upload className="w-5 h-5 text-gray-300 mx-auto mb-1" />
              <p className="text-xs text-gray-400">Kassenbon hier ablegen oder klicken</p>
              <p className="text-[10px] text-gray-300 mt-0.5">Bild oder PDF, auch Smartphone-Foto</p>
            </>
          )}
        </div>

        <button onClick={handleSpeichern} disabled={saving}
          className="mt-3 w-full py-2.5 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: KOOP_RED }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Beleg speichern'}
        </button>
      </div>

      {/* Beleg-Liste */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h4 className="text-sm font-bold text-gray-900 mb-3">Erfasste Belege</h4>
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-300" /></div>
        ) : belege.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">Keine Belege vorhanden</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {belege.map(b => (
              <div key={b.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-800">
                      {new Date(b.datum).toLocaleDateString('de-DE')}
                    </span>
                    {b.beleg_url && (
                      <a href={b.beleg_url} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5">
                        <Image className="w-3 h-3" /> Beleg
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-gray-500">Mahlzeiten: {b.betrag_mahlzeiten.toFixed(2)} EUR</span>
                    <span className="text-[10px] text-gray-400">Aufmerk.: {b.betrag_aufmerksamkeiten.toFixed(2)} EUR</span>
                  </div>
                  {b.notizen && <p className="text-[10px] text-gray-400 mt-0.5">{b.notizen}</p>}
                </div>
                <button onClick={() => loescheBeleg(b.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MONATS-AUSWERTUNG TAB ──────────────────────────────────────────────────

function AuswertungTab({ firma }: { firma: VerpflegungFirma }) {
  const now = new Date();
  const [monat, setMonat] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const { rows, loading } = useMonatsAuswertung(firma, monat);

  const gesamtMahlzeiten = rows.reduce((s, r) => s + r.anzahl_mahlzeiten, 0);
  const gesamtSachbezug = rows.reduce((s, r) => s + r.sachbezugswert, 0);

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <div>
          <label className="text-[11px] font-medium text-gray-500 block mb-1">Monat</label>
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
        </div>
      </div>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-gray-900">{gesamtMahlzeiten}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Mahlzeiten gesamt</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
          <p className="text-2xl font-bold" style={{ color: KOOP_RED }}>{gesamtSachbezug.toFixed(2)} EUR</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Sachbezugswert ({SACHBEZUGSWERT_PRO_TAG} EUR/Tag)</p>
        </div>
      </div>

      {/* Tabelle */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mitarbeiter</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mahlzeiten</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Sachbezugswert</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin text-gray-300 mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-xs text-gray-400">Keine Daten fuer diesen Monat</td></tr>
            ) : (
              rows.map(row => (
                <tr key={row.mitarbeiter_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 text-xs font-medium text-gray-800">{row.mitarbeiter_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 text-right">{row.anzahl_mahlzeiten}</td>
                  <td className="px-4 py-2.5 text-xs font-medium text-right" style={{ color: KOOP_RED }}>{row.sachbezugswert.toFixed(2)} EUR</td>
                </tr>
              ))
            )}
            {rows.length > 0 && (
              <tr className="bg-gray-50 font-bold">
                <td className="px-4 py-2.5 text-xs text-gray-900">Gesamt</td>
                <td className="px-4 py-2.5 text-xs text-gray-900 text-right">{gesamtMahlzeiten}</td>
                <td className="px-4 py-2.5 text-xs text-right" style={{ color: KOOP_RED }}>{gesamtSachbezug.toFixed(2)} EUR</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EXPORT TAB ──────────────────────────────────────────────────────────────

function ExportTab({ firma }: { firma: VerpflegungFirma }) {
  const now = new Date();
  const [monat, setMonat] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const { rows } = useMonatsAuswertung(firma, monat);
  const { belege } = useBelege(firma);
  const [exporting, setExporting] = useState(false);

  const monatBelege = belege.filter(b => b.datum.startsWith(monat));

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`/api/export-verpflegung?firma=${firma}&monat=${monat}`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Verpflegung_${firma}_${monat}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export heruntergeladen');
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div>
          <label className="text-[11px] font-medium text-gray-500 block mb-1">Monat</label>
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
        <h4 className="text-sm font-bold text-gray-900">Export fuer Steuerberater</h4>
        <p className="text-xs text-gray-500">
          ZIP-Datei mit allen Kassenbons, Excel-Tabelle mit Beleg-Zuordnung
          und Uebersicht Mitarbeiter x Mahlzeiten fuer die Lohnabrechnung.
        </p>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-lg font-bold text-gray-900">{monatBelege.length}</p>
            <p className="text-[10px] text-gray-500">Kassenbons</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-lg font-bold text-gray-900">{rows.length}</p>
            <p className="text-[10px] text-gray-500">Mitarbeiter</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-lg font-bold" style={{ color: KOOP_RED }}>
              {rows.reduce((s, r) => s + r.sachbezugswert, 0).toFixed(2)}
            </p>
            <p className="text-[10px] text-gray-500">EUR Sachbezug</p>
          </div>
        </div>

        <button onClick={handleExport} disabled={exporting}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: KOOP_RED }}>
          {exporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Download className="w-4 h-4" />
              ZIP-Export herunterladen
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── HAUPTKOMPONENTE ─────────────────────────────────────────────────────────

type AdminTab = 'belege' | 'auswertung' | 'export';

export default function VerpflegungAdmin() {
  const [tab, setTab] = useState<AdminTab>('belege');
  const [firma, setFirma] = useState<VerpflegungFirma>('Koop');

  const TABS: { id: AdminTab; label: string; icon: any }[] = [
    { id: 'belege', label: 'Beleg-Upload', icon: Upload },
    { id: 'auswertung', label: 'Monats-Auswertung', icon: BarChart3 },
    { id: 'export', label: 'Export', icon: Download },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.06)' }}>
            <FileText className="w-4 h-4" style={{ color: KOOP_RED }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Mitarbeiterverpflegung</h3>
            <p className="text-[10px] text-gray-400">Sachbezugswert-Dokumentation</p>
          </div>
        </div>

        {/* Firma-Toggle */}
        <div className="flex items-center gap-1.5 ml-auto">
          <Building2 className="w-3.5 h-3.5 text-gray-400" />
          {(['LCS', 'Koop'] as VerpflegungFirma[]).map(f => (
            <button key={f} onClick={() => setFirma(f)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${
                firma === f ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              style={firma === f ? { backgroundColor: KOOP_RED } : undefined}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Tab-Bar */}
      <div className="flex bg-gray-100/80 rounded-lg p-0.5">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold transition-all ${
              tab === t.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'
            }`}>
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'belege' && <BelegUploadTab firma={firma} />}
      {tab === 'auswertung' && <AuswertungTab firma={firma} />}
      {tab === 'export' && <ExportTab firma={firma} />}
    </div>
  );
}
